import React, { useState, useEffect } from 'react';
import {
  getPracticeInfo,
  updatePracticePaymentsSettings,
} from '../services/paymentsService';

// Mirror of the server-side ``DEFAULT_INVOICE_SMS_REMINDER_TEMPLATE``
// (see ``payments/backend/server.py``). Used as a hardcoded fallback
// for the textarea placeholder while the ``GET /info`` round-trip is
// in flight, so practices never see "Leave blank to use our default
// copy." flicker into the actual default copy on first paint.
// Source of truth still lives server-side — the parity test in
// ``tests/test_invoice_sms_reminders.py`` keeps the two synced — so
// if you update this string, update server.py too (and vice versa).
const DEFAULT_MESSAGE_TEMPLATE = (
  '{practiceName}: Hi {patientName}, this is a friendly reminder that '
  + 'your balance of {amount} is still outstanding. You can pay '
  + 'securely here: {payLink}'
);

/**
 * RemindersTab
 * ============
 *
 * Practice-level configuration for automated SMS reminders on unpaid
 * invoices. Settings-only for now — the dispatcher that actually sends
 * the texts ships in a follow-up. Persists to
 * ``practice.payments.invoiceSmsReminders`` via the same
 * ``PUT /api/payments/practices/<id>/payments-settings`` endpoint used
 * by SettingsTab, so the storage schema stays in one place even though
 * the UI is split across two tabs.
 *
 * Data shape (mirrors what the server normalises to):
 *
 *     {
 *       enabled: bool,
 *       schedule: [int...],    // days after invoice was sent
 *       messageTemplate: str,
 *     }
 */
const RemindersTab = ({ practiceId }) => {
  const [reminders, setReminders] = useState({
    enabled: false,
    schedule: [],
    messageTemplate: '',
    // 24-hour 'HH:MM' string, interpreted in the practice's local
    // timezone. Default of 11:00 mirrors the server-side default so
    // the input has a sensible value before the first fetch resolves.
    sendTime: '11:00',
  });
  // Server-provided defaults/bounds (default copy, day range, entry cap).
  // Kept separate from the controlled config so we don't round-trip
  // bounds back to the server.
  const [meta, setMeta] = useState({
    // Seed with the hardcoded mirror so the textarea placeholder is
    // never empty during the first paint. Server response overwrites
    // this on hydrate with the canonical value.
    defaultMessageTemplate: DEFAULT_MESSAGE_TEMPLATE,
    defaultSendTime: '11:00',
    maxEntries: 10,
    minDays: 1,
    maxDays: 90,
    maxTemplateLength: 500,
  });
  // Practice IANA timezone (e.g. 'America/New_York') — surfaced next
  // to the time-of-day picker so staff understand the wall-clock
  // semantics. Pulled from ``practice.timezone`` on the same /info
  // call that hydrates the reminders config.
  const [practiceTimezone, setPracticeTimezone] = useState('');
  // Local edit buffers — lifted out so the user can type freely
  // without us PUT-ing a partial value on every keystroke.
  const [newDayDraft, setNewDayDraft] = useState('');
  const [templateDraft, setTemplateDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getPracticeInfo(practiceId);
        if (cancelled) return;
        const incoming = data?.practice?.payments?.invoiceSmsReminders;
        if (data?.practice?.timezone) {
          setPracticeTimezone(data.practice.timezone);
        }
        if (incoming) {
          const hydrated = {
            enabled: !!incoming.enabled,
            schedule: Array.isArray(incoming.schedule)
              ? incoming.schedule
                .map((d) => Number(d))
                .filter((d) => Number.isFinite(d))
              : [],
            messageTemplate: incoming.messageTemplate || '',
            sendTime: incoming.sendTime || '11:00',
          };
          setReminders(hydrated);
          setTemplateDraft(hydrated.messageTemplate);
          setMeta({
            defaultMessageTemplate: (
              incoming.defaultMessageTemplate || DEFAULT_MESSAGE_TEMPLATE
            ),
            defaultSendTime: incoming.defaultSendTime || '11:00',
            maxEntries: Number(incoming.maxEntries) || 10,
            minDays: Number(incoming.minDays) || 1,
            maxDays: Number(incoming.maxDays) || 90,
            maxTemplateLength: Number(incoming.maxTemplateLength) || 500,
          });
        }
      } catch (e) {
        // Non-fatal — the controls fall back to defaults and the user
        // can still configure from scratch. We deliberately don't show
        // an error here to keep the empty state quiet.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [practiceId]);

  // Persist the full ``invoiceSmsReminders`` blob. The server treats
  // the object atomically (any provided sub-key inside it replaces
  // that field), so we always send the merged-locally version.
  // Optimistic update + revert-on-error matches the existing pattern
  // in SettingsTab.
  const saveReminders = async (next) => {
    const prev = reminders;
    setReminders(next);
    setSaving(true);
    setError('');
    try {
      const res = await updatePracticePaymentsSettings(practiceId, {
        invoiceSmsReminders: next,
      });
      const echoed = res?.payments?.invoiceSmsReminders;
      if (echoed) {
        const normalised = {
          enabled: !!echoed.enabled,
          schedule: Array.isArray(echoed.schedule)
            ? echoed.schedule.map((d) => Number(d)).filter(Number.isFinite)
            : [],
          messageTemplate: echoed.messageTemplate || '',
          sendTime: echoed.sendTime || '11:00',
        };
        setReminders(normalised);
        setTemplateDraft(normalised.messageTemplate);
      }
    } catch (e) {
      setReminders(prev);
      setError('Failed to save reminder settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = () => {
    saveReminders({ ...reminders, enabled: !reminders.enabled });
  };

  const addDay = () => {
    const raw = (newDayDraft || '').toString().trim();
    if (!raw) return;
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      setError('Reminder day must be a whole number.');
      return;
    }
    if (parsed < meta.minDays || parsed > meta.maxDays) {
      setError(
        `Reminder day must be between ${meta.minDays} and ${meta.maxDays} days.`,
      );
      return;
    }
    if (reminders.schedule.includes(parsed)) {
      setError(`A reminder is already configured for day ${parsed}.`);
      setNewDayDraft('');
      return;
    }
    if (reminders.schedule.length >= meta.maxEntries) {
      setError(`You can configure at most ${meta.maxEntries} reminder days.`);
      return;
    }
    const nextSchedule = [...reminders.schedule, parsed].sort((a, b) => a - b);
    setNewDayDraft('');
    saveReminders({ ...reminders, schedule: nextSchedule });
  };

  const removeDay = (day) => {
    const nextSchedule = reminders.schedule.filter((d) => d !== day);
    saveReminders({ ...reminders, schedule: nextSchedule });
  };

  const commitTemplate = () => {
    const next = (templateDraft || '').slice(0, meta.maxTemplateLength);
    if (next === reminders.messageTemplate) return;
    saveReminders({ ...reminders, messageTemplate: next });
  };

  // ``<input type="time">`` already enforces the HH:MM shape on
  // modern browsers, so we don't need a draft buffer the way the
  // template textarea does — fire the save on change and let the
  // server reject anything malformed (defence in depth). Empty
  // string is treated as "reset to default".
  const changeSendTime = (value) => {
    const next = (value || '').trim() || meta.defaultSendTime || '11:00';
    if (next === reminders.sendTime) return;
    saveReminders({ ...reminders, sendTime: next });
  };

  // Humanize an IANA timezone string for display. Cosmos stores
  // values like ``America/Los_Angeles`` straight from the tz
  // database — useful as a wire value, jarring as a UI label. We
  // ask the runtime for the long form via Intl
  // (``Pacific Standard Time`` / ``Pacific Daylight Time``) and
  // collapse the DST distinction so the label stays stable
  // year-round (``Pacific Time``). Fallback path mirrors the
  // city-only humanization for older browsers or zones Intl
  // doesn't recognise.
  const formatTimezone = (tz) => {
    if (!tz) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'long',
      }).formatToParts(new Date());
      const tzPart = parts.find((p) => p.type === 'timeZoneName');
      if (tzPart && tzPart.value) {
        return tzPart.value
          .replace(/\bStandard\b/i, '')
          .replace(/\bDaylight\b/i, '')
          .replace(/\s+/g, ' ')
          .trim();
      }
    } catch (e) {
      // Intl rejected the zone (very old runtime or typo'd value) —
      // fall through to the manual split below.
    }
    const segments = tz.split('/');
    const city = (segments[segments.length - 1] || '').replace(/_/g, ' ');
    return city || tz.replace(/_/g, ' ');
  };
  const displayTimezone = formatTimezone(practiceTimezone);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading reminders...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px',
      }}>
        <h4 style={{ margin: '0 0 4px 0', color: '#2b302d' }}>
          Invoice SMS reminders
        </h4>
        <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '0.9rem' }}>
          Automatically text patients to remind them about unpaid invoices.
          Each entry is the number of days <em>after</em> the invoice was sent.
          For example, <strong>3, 7, 14</strong> sends three follow-ups — at
          day 3, day 7, and day 14 — until the invoice is paid or closed.
        </p>

        <SettingToggle
          label="Send SMS reminders for unpaid invoices"
          description="When enabled, the patient receives an SMS reminder on each scheduled day until the invoice is paid or marked closed."
          checked={reminders.enabled}
          disabled={saving}
          onChange={toggleEnabled}
        />

        <div style={{ height: 16 }} />

        <div style={{ marginBottom: 16 }}>
          <div style={{
            fontWeight: 500,
            color: '#2b302d',
            fontSize: 14,
            marginBottom: 8,
          }}>
            Reminder schedule
          </div>

          {reminders.schedule.length === 0 ? (
            <div style={{
              color: '#6b716d',
              fontSize: '0.85rem',
              fontStyle: 'italic',
              marginBottom: 10,
            }}>
              No reminders configured. Add at least one day below to start.
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 10,
            }}>
              {reminders.schedule.map((day) => (
                <span
                  key={day}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 16,
                    backgroundColor: '#e8f0f7',
                    color: '#1f4e79',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  Day {day}
                  <button
                    type="button"
                    onClick={() => removeDay(day)}
                    disabled={saving}
                    aria-label={`Remove reminder day ${day}`}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#1f4e79',
                      cursor: saving ? 'wait' : 'pointer',
                      padding: 0,
                      lineHeight: 1,
                      fontSize: 16,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="number"
              min={meta.minDays}
              max={meta.maxDays}
              step="1"
              value={newDayDraft}
              onChange={(e) => setNewDayDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDay();
                }
              }}
              placeholder="e.g. 7"
              style={{
                width: 100,
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid #d6dbd7',
                fontSize: 14,
              }}
              disabled={saving || reminders.schedule.length >= meta.maxEntries}
            />
            <span style={{ color: '#6b716d', fontSize: 14 }}>
              days after invoice sent
            </span>
            <button
              type="button"
              onClick={addDay}
              disabled={saving
                || !newDayDraft
                || reminders.schedule.length >= meta.maxEntries}
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: 'none',
                backgroundColor: '#1f5a3d',
                color: 'white',
                fontSize: 14,
                fontWeight: 500,
                cursor: (saving || !newDayDraft) ? 'not-allowed' : 'pointer',
                opacity: (saving || !newDayDraft) ? 0.6 : 1,
              }}
            >
              Add
            </button>
          </div>
          <div style={{ color: '#6b716d', fontSize: '0.8rem', marginTop: 6 }}>
            Allowed range: {meta.minDays}–{meta.maxDays} days.
            Up to {meta.maxEntries} reminders per practice.
          </div>
        </div>

        {/* Time-of-day picker.
            -------------------
            Reminders fire at this wall-clock time in the practice's
            local timezone (``practice.timezone`` from Cosmos, shown
            inline below the input so staff can confirm it). Stored as
            a 24-hour 'HH:MM' string; the native <input type="time">
            renders in 12-hour or 24-hour form per the browser locale,
            but the wire format is fixed. */}
        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="invoiceSmsReminderSendTime"
            style={{
              display: 'block',
              fontWeight: 500,
              color: '#2b302d',
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            Time of day
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              id="invoiceSmsReminderSendTime"
              // The dispatcher only fires on the hour (the scheduler
              // ticks ``pt_minute == 0``), so we render hours as a
              // simple <select> instead of <input type="time">. This
              // removes the minute field entirely — there's nothing
              // for the user to fiddle with that could put the value
              // out of sync with the server's strict HH:00 validator.
              value={reminders.sendTime}
              onChange={(e) => changeSendTime(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid #d6dbd7',
                fontSize: 14,
                backgroundColor: 'white',
                cursor: saving ? 'wait' : 'pointer',
              }}
              disabled={saving}
            >
              {Array.from({ length: 24 }, (_, hour) => {
                const hh = String(hour).padStart(2, '0');
                const value = `${hh}:00`;
                // 12-hour display: 0 -> 12 AM, 13 -> 1 PM, etc.
                const display12 = hour === 0
                  ? 12
                  : (hour > 12 ? hour - 12 : hour);
                const ampm = hour < 12 ? 'AM' : 'PM';
                return (
                  <option key={value} value={value}>
                    {`${display12}:00 ${ampm}`}
                  </option>
                );
              })}
            </select>
            <span style={{ color: '#6b716d', fontSize: 14 }}>
              {displayTimezone || '(practice timezone)'}
            </span>
          </div>
          <div style={{ color: '#6b716d', fontSize: '0.8rem', marginTop: 4 }}>
            Reminders are sent on the hour in your practice's local
            timezone. Default {meta.defaultSendTime}
            {displayTimezone ? ` (${displayTimezone})` : ''}.
          </div>
        </div>

        <div style={{ marginBottom: 4 }}>
          <label
            htmlFor="invoiceSmsReminderTemplate"
            style={{
              display: 'block',
              fontWeight: 500,
              color: '#2b302d',
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            Message template
          </label>
          <textarea
            id="invoiceSmsReminderTemplate"
            rows={4}
            value={templateDraft}
            onChange={(e) => setTemplateDraft(
              e.target.value.slice(0, meta.maxTemplateLength),
            )}
            onBlur={commitTemplate}
            placeholder={meta.defaultMessageTemplate
              || 'Leave blank to use our default copy.'}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #d6dbd7',
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
            disabled={saving}
          />
          <div style={{ color: '#6b716d', fontSize: '0.8rem', marginTop: 4 }}>
            Placeholders: <code>{'{patientName}'}</code>,{' '}
            <code>{'{amount}'}</code>, <code>{'{practiceName}'}</code>,{' '}
            <code>{'{payLink}'}</code>.
            Leave blank to use our default copy.
            Max {meta.maxTemplateLength} characters.
          </div>
        </div>

        {error && (
          <div style={{ color: '#b42318', fontSize: '0.85rem', marginTop: 12 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

const SettingToggle = ({ label, description, checked, disabled, onChange }) => (
  <label style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
    margin: 0,
    padding: 0,
    textAlign: 'left',
    cursor: disabled ? 'wait' : 'pointer',
    opacity: disabled ? 0.7 : 1,
  }}>
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={onChange}
      style={{
        flexShrink: 0,
        width: 16,
        height: 16,
        margin: 0,
        marginTop: 3,
        accentColor: '#1f5a3d',
      }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 500, color: '#2b302d', lineHeight: 1.4 }}>{label}</div>
      {description && (
        <div style={{ color: '#6b716d', fontSize: '0.85rem', marginTop: 2, lineHeight: 1.4 }}>
          {description}
        </div>
      )}
    </div>
  </label>
);

export default RemindersTab;
